import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { PaperPlaneRight, Lock } from '@phosphor-icons/react';

export function Chat() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const bottom = useRef();
  const load = async () => { try { const { data } = await api.get('/chat/messages'); setMsgs(data); } catch {} };
  useEffect(() => { if (user?.is_premium || user?.role === 'admin') load(); const t = setInterval(() => { if (user?.is_premium || user?.role === 'admin') load(); }, 5000); return () => clearInterval(t); }, [user]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);
  const send = async (e) => { e.preventDefault(); if (!text.trim()) return; try { await api.post('/chat/send', { message: text }); setText(''); load(); } catch { toast.error('Send failed'); } };

  if (!user?.is_premium && user?.role !== 'admin') return (
    <div className="glass p-12 rounded-xl text-center max-w-lg mx-auto" data-testid="chat-locked"><Lock size={48} className="text-saffron mx-auto mb-4" weight="duotone" /><h2 className="font-heading text-2xl font-bold">Live Chat is Premium</h2><p className="text-muted-foreground mt-2">Upgrade to chat directly with your dedicated Account Manager.</p></div>
  );

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)]" data-testid="chat-page">
      <div className="glass rounded-t-xl p-4 border-b border-white/5"><h2 className="font-heading font-bold text-lg">Account Manager</h2><div className="flex items-center gap-2 text-xs text-green-400 mt-1"><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />Online</div></div>
      <div className="glass flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.length === 0 && <div className="text-center text-muted-foreground text-sm mt-8">Send your first message. Our team responds within minutes.</div>}
        {msgs.map(m => (
          <div key={m.id} className={`flex ${m.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[70%] p-3 rounded-lg ${m.sender_id === user.id ? 'bg-saffron text-white' : 'bg-surface-2'}`}>
              <div className="text-xs opacity-60 mb-1">{m.sender_name}</div>
              <div className="text-sm">{m.message}</div>
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="glass rounded-b-xl p-3 flex gap-2 border-t border-white/5">
        <Input value={text} onChange={e => setText(e.target.value)} placeholder="Type your message..." className="bg-surface-base border-white/10" data-testid="chat-input" />
        <Button type="submit" className="bg-saffron hover:bg-saffron-hover text-white" data-testid="chat-send"><PaperPlaneRight size={16} /></Button>
      </form>
    </div>
  );
}

export function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ full_name: user?.full_name || '', phone: user?.phone || '', country: user?.country || '' });
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  useEffect(() => { if (user) setForm({ full_name: user.full_name, phone: user.phone || '', country: user.country || '' }); }, [user]);
  const save = async (e) => { e.preventDefault(); try { await api.patch('/profile', form); toast.success('Profile updated'); refresh(); } catch { toast.error('Failed'); } };
  const changePw = async (e) => { e.preventDefault(); try { await api.post('/profile/change-password', pw); toast.success('Password changed'); setPw({current_password:'', new_password:''}); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } };

  return (
    <div className="max-w-3xl space-y-6" data-testid="profile-page">
      <div><div className="data-label text-saffron">Profile</div><h1 className="font-heading text-3xl font-bold mt-1">Your Account</h1></div>
      <div className="glass p-6 rounded-lg flex items-center gap-4">
        <div className="w-16 h-16 rounded-full gradient-orange flex items-center justify-center text-2xl font-bold">{user?.full_name?.[0]}</div>
        <div className="flex-1">
          <div className="font-heading font-bold text-xl">{user?.full_name}</div>
          <div className="text-sm text-muted-foreground">{user?.email}</div>
          <div className="flex gap-2 mt-2 text-xs">
            <span className={`px-2 py-0.5 rounded ${user?.is_premium ? 'bg-saffron/20 text-saffron' : 'bg-white/5 text-muted-foreground'}`}>{user?.is_premium ? '⭐ Premium' : 'Free'}</span>
            <span className={`px-2 py-0.5 rounded bg-white/5 text-muted-foreground capitalize`}>Verif: {user?.verification_status?.replace('_',' ')}</span>
          </div>
        </div>
      </div>
      <form onSubmit={save} className="glass p-6 rounded-lg space-y-3">
        <h3 className="font-heading font-bold text-lg">Personal Details</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="data-label">Full Name</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
          <div><Label className="data-label">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
          <div className="md:col-span-2"><Label className="data-label">Country</Label><Input value={form.country} onChange={e => setForm({...form, country: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
        </div>
        <Button type="submit" className="bg-saffron hover:bg-saffron-hover text-white">Save Profile</Button>
      </form>
      <form onSubmit={changePw} className="glass p-6 rounded-lg space-y-3">
        <h3 className="font-heading font-bold text-lg">Security</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="data-label">Current Password</Label><Input type="password" required value={pw.current_password} onChange={e => setPw({...pw, current_password: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
          <div><Label className="data-label">New Password</Label><Input type="password" required minLength={6} value={pw.new_password} onChange={e => setPw({...pw, new_password: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
        </div>
        <Button type="submit" className="bg-saffron hover:bg-saffron-hover text-white">Change Password</Button>
      </form>
    </div>
  );
}
